import React, { useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import Notification from './Notification';

let _idCounter = 0;

/**
 * NotificationContainer
 *
 * Mount once at app root. Renders all active notifications in a fixed
 * bottom-left stack. Use the `useNotification` hook to push toasts.
 */
export const NotificationContainer = () => {
  /* We use a module-level ref so the hook can push into it. */
  return null; // see standalone approach below
};

/**
 * Standalone imperative wrapper.
 *
 * Usage:
 *   import { NotificationProvider, useNotification } from '../_designSystem/Notification/useNotification';
 *
 *   // In App root:
 *   <NotificationProvider />
 *
 *   // Anywhere:
 *   const notify = useNotification();
 *   notify({ type: 'success', title: 'Saved!', message: 'Profile updated.' });
 */

const listeners = new Set();
let notifications = [];

function emitChange() {
  listeners.forEach((fn) => fn([...notifications]));
}

export function pushNotification({ type = 'success', title = '', message = '', duration = 4000, closeLabel }) {
  const id = ++_idCounter;
  notifications = [...notifications, { id, type, title, message, duration, closeLabel }];
  emitChange();
  return id;
}

export function removeNotification(id) {
  notifications = notifications.filter((n) => n.id !== id);
  emitChange();
}

/**
 * Hook that returns a `notify(opts)` function.
 */
export function useNotification() {
  return useCallback((opts) => pushNotification(opts), []);
}

/**
 * Provider component — render once in your app tree.
 * Creates a portal with the fixed notification container.
 */
export const NotificationProvider = () => {
  const [items, setItems] = useState([]);
  const containerRef = useRef(null);

  /* Subscribe to store */
  React.useEffect(() => {
    const handler = (next) => setItems(next);
    listeners.add(handler);
    return () => listeners.delete(handler);
  }, []);

  if (items.length === 0) return null;

  return ReactDOM.createPortal(
    <div className="ds-notification-container" ref={containerRef}>
      {items.map((n) => (
        <Notification
          key={n.id}
          type={n.type}
          title={n.title}
          message={n.message}
          duration={n.duration}
          closeLabel={n.closeLabel}
          onClose={() => removeNotification(n.id)}
        />
      ))}
    </div>,
    document.body,
  );
};
